import React from 'react';
import { FaSearch, FaSort } from 'react-icons/fa';
import ComponentLoader from '../ComponentLoader';

const ComponentsGrid = ({
  searchQuery,
  filteredComponents,
  components,
  isReordering,
  setIsReordering,
  effectiveScale,
  animateComponents,
  dragOverIndex,
  draggedComponent,
  handleDragStart,
  handleDragEnd,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleUpdateComponent,
  handleDeleteComponent,
  canEditIdea,
  refreshSidebar,
  id,
  setShowSearch,
  setSearchQuery
}) => {
  const columnCounts = {
    1: 'columns-1',
    2: 'columns-2',
    3: 'columns-3',
    4: 'columns-3 md:columns-4',
    5: 'columns-3 md:columns-4 xl:columns-5',
    6: 'columns-3 md:columns-4 xl:columns-6',
  };

  return (
    <>
      {/* No Results Message - Outside columns to prevent splitting */}
      {!isReordering && searchQuery.trim() && filteredComponents.length === 0 && components.length > 0 && (
        <div className="text-center py-12">
          <div className="mb-4">
            <FaSearch className="text-6xl text-[var(--text-disabled)] mx-auto mb-4 opacity-50" />
          </div>
          <h3 className="text-xl font-semibold text-[var(--text-primary)] mb-2">No components found</h3>
          <p className="text-[var(--text-secondary)] mb-4">
            No components match your search for "<span className="font-medium">{searchQuery}</span>"
          </p>
          <button
            onClick={() => {
              setSearchQuery('');
              setShowSearch(false);
            }}
            className="text-[var(--primary)] hover:text-[var(--primary-dark)] transition-colors duration-200"
          >
            Clear search
          </button>
        </div>
      )}

      <div key={`components-${id}`} className={`columns ${columnCounts[effectiveScale]} gap-6 pb-6 break-inside-avoid transition-all duration-500 ease-in-out`}>
        {isReordering && components.length > 0 && (
          <div 
            className={`w-full h-8 border-2 border-dashed rounded-lg mb-4 transition-all duration-200 ${
              dragOverIndex === -1 ? 'border-[var(--primary)] bg-[var(--primary)]/10 scale-105' : 'border-[var(--primary)]/30'
            } flex items-center justify-center`}
            onDragOver={(e) => handleDragOver(e, -1)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, 0)}
          >
            <span className="text-xs text-[var(--text-secondary)]">Drop here to move to top</span>
          </div>
        )}

        {(isReordering ? components : filteredComponents).map((component, index) => {
          const isDragOver = dragOverIndex === index;
          const isDragging = draggedComponent?.index === index;
          
          return (
            <div 
              key={component.id} 
              className={`component-wrapper mb-6 break-inside-avoid transition-all duration-200 ${
                animateComponents ? 'animate-float-in-bounce-zoom-subtle' : 'opacity-0'
              } ${
                isReordering ? 'cursor-move rounded-lg p-2' : 'hover:scale-105'
              } ${
                isDragOver && isReordering ? 'border-2 border-dashed border-[var(--primary)] bg-[var(--primary)]/10 scale-105' : ''
              } ${
                isDragging ? 'opacity-50 scale-95' : ''
              } ${
                isReordering && !isDragging ? 'border-2 border-dashed border-[var(--primary)]/30 hover:border-[var(--primary)] hover:bg-[var(--primary)]/5' : ''
              }`}
              draggable={isReordering}
              onDragStart={(e) => handleDragStart(e, component, index)}
              onDragEnd={handleDragEnd}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragLeave={handleDragLeave}
              onDrop={(e) => handleDrop(e, index)}
            >
              {isReordering && (
                <div className="flex items-center justify-between mb-2 p-2 bg-[var(--bg-hover)] rounded transition-colors duration-200">
                  <span className="text-xs text-[var(--text-secondary)] font-medium">Position: {index + 1}</span>
                  <div className="flex items-center gap-2">
                    <FaSort className="text-[var(--primary)] cursor-move animate-pulse" />
                    <span className="text-xs text-[var(--text-secondary)]">Drag to reorder</span>
                  </div>
                </div>
              )}
              <ComponentLoader
                component={component}
                onUpdate={(newData) => handleUpdateComponent(component.id, newData)}
                onDelete={handleDeleteComponent}
                isOwner={canEditIdea}
                refreshSidebar={refreshSidebar}
                onReorder={() => setIsReordering(true)}
              />
            </div>
          );
        })}
        
        {isReordering && components.length > 0 && (
          <div 
            className={`w-full h-8 border-2 border-dashed rounded-lg mt-4 transition-all duration-200 ${
              dragOverIndex === components.length ? 'border-[var(--primary)] bg-[var(--primary)]/10 scale-105' : 'border-[var(--primary)]/30'
            } flex items-center justify-center`}
            onDragOver={(e) => handleDragOver(e, components.length)}
            onDragLeave={handleDragLeave}
            onDrop={(e) => handleDrop(e, components.length)}
          >
            <span className="text-xs text-[var(--text-secondary)]">Drop here to move to bottom</span>
          </div>
        )}
      </div>
    </>
  );
};

export default ComponentsGrid;
